import React, { useState, useEffect } from "react";

import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import {
  Key,
  Cloud,
  PersonAdd,
  PersonRemove,
  Remove,
  Info,
  CheckCircle,
  ArrowForward,
  Add,
  CloudDone,
  Adjust,
  Computer,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Container,
  Divider,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
} from "@mui/material";

import Loading from '../components/loader';
import useClusters from '../hooks/clusters';
import useProfile from '../hooks/profile';
import usePlausible from '../hooks/usePlausible';
import { ClusterParams, User } from '../types/cluster'; 

let id = 0;

// Cluster size definitions
const clusterSizes = {
  sm: {
    label: "Small",
    description: "Suitable for development, testing, and small applications",
    specs: "2 vCPUs, 4GB RAM",
    icon: <Computer fontSize="small" />,
  },
  lg: {
    label: "Large",
    description: "Suitable for production workloads and larger applications",
    specs: "4 vCPUs, 8GB RAM",
    icon: <Computer fontSize="small" />,
  },
};

// Steps for the cluster creation process
const steps = ["Cluster Details", "User Configuration", "Review & Create"];

export const CreateCluster = () => {
  const navigate = useNavigate();
  const { loading, profile } = useProfile();
  const {
    createCluster,
    checkExists,
    listDNS
  } = useClusters();
  const { enqueueSnackbar } = useSnackbar();
  const plausible = usePlausible();
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Keep existing state management to ensure functionality
  const [name, setName] = useState("a");
  const [domain, setDomain] = useState("");

  const [data, setData] = useState({
    size: "sm",
    users: [
      {
        id,
        user: "astuart",
        email: "andrew@microcumulus.com",
        password: "password",
        keys: [
          "ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABAQCaeFPZ9NxLG4wvKr6jIZq2madY17cQoxpRSWaufrUNTmgHmkDrDtPkXAQ+0atcNzgGkcMFviJbh0yY9sW56HlzEecqcKK/oR06AhWZgKjedQAU/ELZotX0q2BEVtNBwL/I9tHgShpA0kmki5pLMQD/9fONfGvdHlsNqv/SOTn6V2fWaRHkVgP91Wtn3ymicjMh8dA9SHLGhN13pe7gLKM+INOHuiYvwXXA0Z/8ne9keBsKE4UAznj3gkyTf1QWA3sfL9UIPEXqkabp63tDgG8Ay8j4H4E05fRPeLM3Zxzl9F9JVTwiG81O6catfgJ77H3tXUryAswl89+oO5t/Q5TB andrew@desktop",
        ],
      },
    ],
  } as ClusterParams);

  const [conflict, setConflict] = useState(false);

  // Modified conflict check logic - only check on blur
  const checkNameConflict = () => {
    if (name && domain) {
      checkExists(`${name}.${domain}`)
        .then(f => setConflict(f))
        .catch(err => enqueueSnackbar(`Failed to check cluster name availability: ${err.message}`, { variant: 'error' }));
    }
  };

  // Check on mount and if domain changes
  useEffect(() => {
    if (name && domain) {
      checkNameConflict();
    }
  }, [domain, name]);

  // Modified DNS loading logic to prevent re-renders
  const [dns, setDNS] = useState([] as string[]);
  const [dnsLoaded, setDnsLoaded] = useState(false);

  // Load DNS domains only once when component mounts
  useEffect(() => {
    if (!dnsLoaded) {
      listDNS()
        .then((f) => {
          setDNS(f);
          if (!domain && f.length > 0) {
            setDomain(f[0]);
          }
          setDnsLoaded(true);
        })
        .catch(err => enqueueSnackbar(`Failed to load available domains: ${err.message}`, { variant: 'error' }));
    }
  }, [loading, dnsLoaded, domain]);

  if (loading) {
    return <Loading />;
  }

  if (!profile.stripe) {
    navigate("/signup");
    return <Loading />;
  }
  if (!(profile.aws.id && profile.aws.canAssume)) {
    navigate("/link");
    return <Loading />;
  }

  const handleSubmit = async () => {
    setSaving(true);
    data.name = name + "." + domain;

    try {
      // Track the start of cluster creation
      plausible.trackEvent("cluster_creation_started", {
        clusterName: data.name,
        clusterSize: data.size,
      });

      const cluster = await createCluster(data);
      setData(cluster);
      setSaving(false);

      // Show success message
      enqueueSnackbar("Cluster created successfully!", {
        variant: "success",
        autoHideDuration: 3000,
      });

      // Track successful cluster creation
      plausible.trackEvent("cluster_created", {
        clusterName: data.name,
        clusterSize: data.size,
      });

      // Navigate to the new cluster
      navigate(`/cluster/${data.name}`);
    } catch (e: any) {
      setSaving(false);

      // Track failed cluster creation
      plausible.trackEvent("cluster_creation_failed", {
        clusterName: data.name,
        clusterSize: data.size,
        error: e.message,
      });
      
      // Show error message
      enqueueSnackbar(`Failed to create cluster: ${e.message}`, {
        variant: 'error'
      });
    }
  };

  // Add a new user
  const addUser = () => {
    data.users.push({
      id: ++id,
      user: "",
      email: "",
      password: "",
      keys: [""],
    } as User);
    setData({ ...data });
  };

  // Step navigation
  const handleNext = () => {
    const nextStep = activeStep + 1;
    setActiveStep(nextStep);

    // Track wizard step completion
    plausible.trackEvent("cluster_creation_step_completed", {
      step: steps[activeStep],
      nextStep: steps[nextStep],
      stepNumber: activeStep + 1,
    });
  };

  const handleBack = () => {
    const prevStep = activeStep - 1;
    setActiveStep(prevStep);

    // Track going back in wizard
    plausible.trackEvent("cluster_creation_step_back", {
      fromStep: steps[activeStep],
      toStep: steps[prevStep],
      stepNumber: activeStep,
    });
  };

  // Validate step completion
  const canProceedFromStep1 = name && domain && !conflict;

  const canProceedFromStep2 = data.users.every(
    (user) =>
      user.user &&
      user.email &&
      user.password &&
      user.keys.some((key) => key.trim() !== ""),
  );

  // Individual user component
  const users = data.users.map((u, i) => {
    const updateUser = (newu: User) => {
      data.users.splice(i, 1, newu);
      setData({ ...data });
    };

    const remove = () => {
      if (data.users.length > 1) {
        data.users.splice(i, 1);
        setData({ ...data });
      } else {
        enqueueSnackbar("At least one user is required", {
          variant: "warning",
        });
      }
    };

    return (
      <Card
        key={u.id}
        sx={{
          my: 3,
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          position: "relative",
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <CardHeader
          title={u.user ? `User: ${u.user}` : `User ${i + 1}`}
          action={
            <Tooltip title="Remove User">
              <IconButton
                onClick={remove}
                size="large"
                sx={{
                  marginRight: 1,
                  color: "#d32f2f", // deeper red color
                }}
              >
                <PersonRemove />
              </IconButton>
            </Tooltip>
          }
        />
        <Divider />
        <CardContent>
          <UserField user={u} onUpdate={updateUser} />
        </CardContent>
      </Card>
    );
  });

  // Size selection component
  const SizeSelector = () => (
    <Stack spacing={2} sx={{ mb: 4 }}>
      <Typography variant="h6">Cluster Size</Typography>
      <Grid container spacing={2}>
        {Object.entries(clusterSizes).map(([sizeKey, sizeInfo]) => (
          <Grid item xs={12} sm={6} key={sizeKey}>
            <Card
              sx={{
                cursor: "pointer",
                border: "2px solid",
                borderColor: data.size === sizeKey ? "primary.main" : "divider",
                boxShadow:
                  data.size === sizeKey
                    ? "0 0 0 2px rgba(67, 211, 255, 0.2)"
                    : "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor:
                    data.size === sizeKey ? "primary.main" : "primary.light",
                  transform: "translateY(-2px)",
                },
                position: "relative",
                overflow: "visible",
                height: "100%",
              }}
              onClick={() => setData({ ...data, size: sizeKey })}
            >
              {data.size === sizeKey && (
                <Chip
                  icon={<CheckCircle fontSize="small" />}
                  label="Selected"
                  color="primary"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -10,
                    right: 16,
                    fontWeight: "bold",
                  }}
                />
              )}
              <CardContent>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {sizeInfo.icon}
                    <Typography variant="h6">{sizeInfo.label}</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {sizeInfo.description}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {sizeInfo.specs}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  // Cluster name and domain component
  const ClusterNameInput = () => (
    <Stack spacing={2} sx={{ mb: 4 }}>
      <Typography variant="h6">Cluster Name</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Your cluster will be accessible at{" "}
        <strong>
          {name || "<prefix>"}.{domain || "<domain>"}
        </strong>
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Cluster Name"
            variant="outlined"
            defaultValue={name}
            // Using onBlur to update state when focus leaves the field
            onBlur={(e) => {
              const newValue = e.target.value;
              setName(newValue);
              // Check for conflicts after updating
              setTimeout(() => checkNameConflict(), 0);
            }}
            // Allow typing without triggering state updates on every keystroke
            onChange={(_e) => {
              // Just let the default input behavior happen
              // The state will be updated on blur
            }}
            error={conflict}
            helperText={
              conflict
                ? "This name is already taken"
                : "Use lowercase letters, numbers, and hyphens only"
            }
            placeholder="my-cluster"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Adjust fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!domain}>
            <InputLabel>Domain</InputLabel>
            <Select
              label="Domain"
              value={domain}
              // Keep normal onChange for dropdown since it doesn't have the same issue
              onChange={(s) => setDomain(s.target.value)}
            >
              {dns.map((domainOption) => (
                <MenuItem value={domainOption} key={domainOption}>
                  {domainOption}
                </MenuItem>
              ))}
            </Select>
            {!domain && (
              <Typography variant="caption" color="error">
                Domain is required
              </Typography>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </Stack>
  );

  // Render step content based on active step
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <ClusterNameInput />
            <Divider sx={{ my: 4 }} />
            <SizeSelector />
          </>
        );
      case 1:
        return (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 3 }}
            >
              <Typography variant="h6">User Configuration</Typography>
              <Button
                variant="outlined"
                startIcon={<PersonAdd />}
                onClick={addUser}
                sx={{ borderRadius: 2 }}
              >
                Add User
              </Button>
            </Stack>
            {users}
          </>
        );
      case 2:
        return (
          <>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Review Configuration
            </Typography>

            <Card sx={{ mb: 3, borderRadius: 2 }}>
              <CardHeader title="Cluster Details" />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        Cluster Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {name}.{domain}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">
                        Cluster Size
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {
                          clusterSizes[data.size as keyof typeof clusterSizes]
                            .label
                        }{" "}
                        (
                        {
                          clusterSizes[data.size as keyof typeof clusterSizes]
                            .specs
                        }
                        )
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, borderRadius: 2 }}>
              <CardHeader
                title="Users"
                subheader={`${data.users.length} user${data.users.length > 1 ? "s" : ""} configured`}
              />
              <Divider />
              <CardContent>
                <Stack spacing={2}>
                  {data.users.map((user, index) => (
                    <Paper
                      key={user.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: "background.default",
                        borderRadius: 2,
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              Username
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {user.user}
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              Email
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {user.email}
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              SSH Keys
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {user.keys.filter((k) => k.trim() !== "").length}{" "}
                              key(s)
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mb: 3 }}>
              Creating a new cluster may take several minutes. You'll be
              redirected to the cluster dashboard when it's ready.
            </Alert>
          </>
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          background:
            "linear-gradient(to bottom, rgba(26, 30, 35, 0.8), rgba(26, 30, 35, 1))",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          sx={{
            mb: 4,
            fontWeight: 700,
            background: "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Create a New Cluster
        </Typography>

        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 4,
            "& .MuiStepIcon-root.Mui-active": {
              color: "primary.main",
            },
            "& .MuiStepIcon-root.Mui-completed": {
              color: "success.main",
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ mb: 4 }} />

        <Box>{getStepContent(activeStep)}</Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>

          <Box>
            {activeStep === steps.length - 1 ? (
              <LoadingButton
                loading={saving}
                variant="contained"
                onClick={handleSubmit}
                disabled={!canProceedFromStep2}
                endIcon={<CloudDone />}
                loadingPosition="end"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  background:
                    "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #38b8df 0%, #3be07f 100%)",
                  },
                }}
              >
                Create Cluster
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForward />}
                disabled={
                  activeStep === 0 ? !canProceedFromStep1 : !canProceedFromStep2
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  background:
                    "linear-gradient(90deg, #43d3ff 0%, #46ff93 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, #38b8df 0%, #3be07f 100%)",
                  },
                }}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

interface UserFieldParams {
  user: User;
  onUpdate: { (u: User): void };
}

const UserField = ({ user, onUpdate }: UserFieldParams) => {
  const handleUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ ...user, [e.target.name]: e.target.value });
  };

  const addKey = () => {
    user.keys.push("");
    onUpdate({ ...user });
  };

  const keys = user.keys.map((key, i) => {
    const update = (e: React.ChangeEvent<HTMLInputElement>) => {
      user.keys.splice(i, 1, e.target.value);
      onUpdate({ ...user });
    };

    const remove = () => {
      if (user.keys.length > 1) {
        user.keys.splice(i, 1);
        onUpdate({ ...user });
      } else {
        // If it's the last key, just clear it instead of removing
        user.keys[0] = "";
        onUpdate({ ...user });
      }
    };

    return (
      <Box key={i} sx={{ position: "relative", mb: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          label={`SSH Key ${i + 1}`}
          value={key}
          onChange={update}
          placeholder="ssh-rsa AAAAB3NzaC1yc2EAAA..."
          variant="outlined"
          sx={{ mb: 1 }}
        />
        <Fab
          size="small"
          onClick={remove}
          sx={{
            position: "absolute",
            top: -10,
            right: -10,
            bgcolor: "#d32f2f", // deeper red color
            "&:hover": {
              bgcolor: "#b71c1c", // even deeper red on hover
            },
          }}
        >
          <Remove />
        </Fab>
      </Box>
    );
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Username"
          name="user"
          value={user.user}
          onChange={handleUpdate}
          variant="outlined"
          required
          placeholder="johndoe"
          helperText="Linux username for this user"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={user.email}
          onChange={handleUpdate}
          variant="outlined"
          required
          placeholder="user@example.com"
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Password"
          name="password"
          type="password"
          value={user.password}
          onChange={handleUpdate}
          variant="outlined"
          required
          helperText="Used for initial login only"
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="subtitle1">SSH Keys</Typography>
            <Tooltip title="SSH keys allow secure access to your cluster">
              <Info fontSize="small" color="action" />
            </Tooltip>
          </Stack>

          {keys}

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addKey}
            size="small"
            sx={{ mt: 1, borderRadius: 2 }}
          >
            Add Another Key
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};